import { plentyOneApi } from '@/services/plentyOneApi';

import { logger } from '@/services/logger';
/**
 * Represents a single order summary item in the order history list.
 */
export interface OrderSummary {
  id: string;
  orderNumber: string;
  date: string; // ISO 8601 date string
  status: string;
  totalAmount: number;
  itemCount: number;
  items: Array<{
    name: string;
    quantity: number;
    sku: string;
  }>;
  customerId?: string;
}

/**
 * Represents pagination metadata included in the API response.
 */
export interface PaginationMeta {
  currentPage: number;
  totalPages: number;
  total: number;
  itemsPerPage: number;
}

/**
 * Defines the parameters for the getOrders function.
 */
export interface GetOrdersParams {
  page: number;
  itemsPerPage: number;
  search?: string;
  userId?: string;
  contactId?: number;
  isDemoUser: boolean;
}

/**
 * Defines the structure of the successful response from the getOrders service.
 */
export interface GetOrdersResponse {
  orders: OrderSummary[];
  pagination: PaginationMeta;
}

/**
 * Demo order data for testing
 */
const getDemoOrderHistory = (): OrderSummary[] => [
  {
    id: "DEMO-ORDER-001",
    orderNumber: "ORDER-2024001",
    date: "2024-01-15T10:00:00Z",
    status: "delivered",
    totalAmount: 299.99,
    itemCount: 1,
    items: [
      {
        name: "Business Socks",
        sku: "BS-001",
        quantity: 2
      }
    ]
  },
  {
    id: "DEMO-ORDER-002", 
    orderNumber: "ORDER-2024002",
    date: "2024-01-10T14:30:00Z",
    status: "shipped",
    totalAmount: 159.50,
    itemCount: 1,
    items: [
      {
        name: "Comfort Socks",
        sku: "CS-001", 
        quantity: 3
      }
    ]
  },
  {
    id: "DEMO-ORDER-003",
    orderNumber: "ORDER-2024003",
    date: "2024-01-05T09:15:00Z",
    status: "processing",
    totalAmount: 89.99,
    itemCount: 1,
    items: [
      {
        name: "Sport Socks",
        sku: "SS-001",
        quantity: 1
      }
    ]
  }
];

/**
 * Fetches a paginated list of demo orders with search support.
 * This simulates a real API call with a delay.
 */
const getDemoOrders = async (params: GetOrdersParams): Promise<GetOrdersResponse> => {
  const { page, itemsPerPage, search = "" } = params;
  let demoOrders = getDemoOrderHistory();
  
  // Apply search filter if provided
  if (search) {
    demoOrders = demoOrders.filter(order => 
      order.orderNumber.toLowerCase().includes(search.toLowerCase()) ||
      order.items.some(item => 
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.sku.toLowerCase().includes(search.toLowerCase())
      )
    );
  }
  
  const totalEntries = demoOrders.length;
  const totalPages = Math.ceil(totalEntries / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const orders = demoOrders.slice(startIndex, endIndex);

  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 300));

  return {
    orders,
    pagination: {
      currentPage: page,
      totalPages,
      total: totalEntries,
      itemsPerPage,
    },
  };
};

/**
 * Fetches a paginated list of real orders from the ERP API.
 */
const getRealOrders = async (params: GetOrdersParams): Promise<GetOrdersResponse> => {
  const { page, itemsPerPage, search, userId, contactId } = params;
  
  if (!contactId) {
    throw new Error("Knitido Customer ID is required for fetching real orders");
  }
  
  // Call the existing API method
  const response = await plentyOneApi.getOrders({
    customerId: userId || "unknown",
    contactId,
    page,
    itemsPerPage,
    search
  });
  
  return {
    orders: response.orders,
    pagination: {
      currentPage: response.currentPage,
      totalPages: response.totalPages,
      total: response.total,
      itemsPerPage,
    }
  };
};

/**
 * Main service function that fetches orders based on user type.
 * Delegates to either demo or real API based on isDemoUser flag.
 */
export const fetchOrders = async (params: GetOrdersParams): Promise<GetOrdersResponse> => {
  try {
    if (params.isDemoUser) {
      return await getDemoOrders(params);
    } else {
      return await getRealOrders(params);
    }
  } catch (error) {
    logger.error('Error fetching orders:', error);
    throw error; // Re-throw for TanStack Query to handle
  }
};

/**
 * Helper function to generate query key for TanStack Query
 */
export const getOrdersQueryKey = (
  userId: string | undefined, 
  contactId: number | undefined,
  page: number, 
  itemsPerPage: number, 
  search: string,
  isDemoUser: boolean
) => {
  return ['orders', { userId, contactId, page, itemsPerPage, search, isDemoUser }] as const;
};